/**
 * Versus splash (design 05 · UI — VERSUS Splash, VCkLa): the screen tears along an animated diagonal into a red
 * HOME field and a blue AWAY field, the two heads slam in from opposite corners, their gloves clash in the middle
 * (ball vs stiff-arm) under a huge chrome-gold VS burst, name banners, a venue chyron and the tale of the tape.
 * It starts the pending match after ~3 s (any confirm skips).
 */
import './versus.css';
import { useEffect, useRef } from 'preact/hooks';
import { characterById, MatchFormat, SpecialsMode, specialInfo } from '@hf/shared';
import { useActions, useServices } from '../actions';
import { Glove, Head, Hints, Rays } from '../components';
import { pendingMatch, type PendingMatch } from '@hf/client-core/ui-model/flow';
import { useMenuLayer } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { ui } from '@hf/client-core/ui-model/store';
import { pendingFromOnline } from '@hf/client-core/ui-model/screens';
export { pendingFromOnline } from '@hf/client-core/ui-model/screens';

export const VERSUS_MS = 3400;

const currentPending = (): PendingMatch | null => {
  const p = pendingMatch.peek();
  if (p) return p;
  const s = ui.matchSetup.peek();
  return s?.online ? pendingFromOnline(s, ui.net.peek().rating?.rating) : null;
};

export function Versus() {
  const actions = useActions();
  const { audio } = useServices();
  void pendingMatch.value;
  void ui.matchSetup.value;
  const pending = currentPending();
  const started = useRef(false);

  const go = (): void => {
    if (started.current) return;
    started.current = true;
    const p = currentPending();
    if (!p) {
      actions.goto('main_menu');
      return;
    }
    pendingMatch.value = null;
    actions.startMatch(p.setup);
  };

  useEffect(() => {
    if (!pending) {
      actions.goto('main_menu');
      return;
    }
    playSfx(audio, UI_SFX.whoosh);
    const slam = setTimeout(() => playSfx(audio, UI_SFX.slam), 520);
    // Online matches start on the server's clock: the splash never delays them past the handshake.
    const ms = pending.setup.online ? 2600 : ui.settings.peek().reducedMotion ? 1800 : VERSUS_MS;
    const t = setTimeout(go, ms);
    return () => {
      clearTimeout(t);
      clearTimeout(slam);
    };
  }, []);

  useMenuLayer((a) => {
    if (a === 'confirm' || a === 'pause') go();
  });

  if (!pending) return null;
  const { setup } = pending;
  const [c0, c1] = setup.config.characters;
  const d0 = characterById(c0);
  const d1 = characterById(c1);
  const norm = setup.config.normalizedStats;
  const specials = setup.config.specials !== SpecialsMode.OFF;
  const stat = (v: number): number => (norm ? 3 : v);
  const tape: [string, string, string][] = [
    [
      specials ? specialInfo(d0.signature).name : 'OFF',
      'SPECIAL',
      specials ? specialInfo(d1.signature).name : 'OFF',
    ],
    [`${stat(d0.stats.spd)}/5`, 'SPEED', `${stat(d1.stats.spd)}/5`],
    [`${stat(d0.stats.arm)}/5`, 'ARM', `${stat(d1.stats.arm)}/5`],
    [`${stat(d0.stats.tkl)}/5`, 'TACKLE', `${stat(d1.stats.tkl)}/5`],
  ];

  return (
    <div class="hf-screen hf-versus" data-screen="versus" onClick={go}>
      <div class="hf-versus__field p1">
        <Rays a="#ff4a52" b="#8c1020" rays={22} x="22%" y="62%" speed={30} vignette={false} />
      </div>
      <div class="hf-versus__field p2">
        <Rays a="#3d8bff" b="#0d2a70" rays={22} x="78%" y="38%" speed={30} reverse vignette={false} />
      </div>
      <div class="hf-versus__speed" aria-hidden="true" />
      <div class="hf-versus__slash" aria-hidden="true" />
      <div class="hf-versus__bolt" aria-hidden="true" />
      <div class="hf-versus__flash" aria-hidden="true" />

      <div class="hf-versus__head p1">
        <Head character={c0} expression="determined" class="hf-versus__headart" />
      </div>
      <div class="hf-versus__head p2">
        <Head character={c1} expression="determined" flip class="hf-versus__headart" />
      </div>
      <div class="hf-versus__glove p1">
        <Glove side="home" pose="fist" class="hf-versus__gloveart" />
      </div>
      <div class="hf-versus__glove p2">
        <Glove side="away" pose="fist" class="hf-versus__gloveart" />
      </div>
      <div class="hf-versus__vs t-display" aria-label="versus">
        <span>VS</span>
      </div>

      <div class="hf-versus__banner p1">
        <div class="hf-versus__name t-display">{d0.name}</div>
        <div class="hf-versus__sub t-broadcast">{pending.tags[0]}</div>
      </div>
      <div class="hf-versus__banner p2">
        <div class="hf-versus__name t-display">{d1.name}</div>
        <div class="hf-versus__sub t-broadcast">{pending.tags[1]}</div>
      </div>

      <div class="hf-versus__venue t-broadcast">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7zm0 9.5A2.5 2.5 0 1 1 12 6a2.5 2.5 0 0 1 0 5.5z"
            fill="currentColor"
          />
        </svg>
        {pending.venue}
      </div>

      <div class="hf-tape hf-panel">
        <div class="hf-tape__title t-broadcast">TALE OF THE TAPE</div>
        {tape.map(([a, l, b]) => (
          <div class="hf-tape__row" key={l}>
            <span class="a t-broadcast">{a}</span>
            <span class="l">{l}</span>
            <span class="b t-broadcast">{b}</span>
          </div>
        ))}
        <div class="hf-tape__row fmt">
          <span class="l">
            {setup.config.format === MatchFormat.CLASSIC
              ? '2 HALVES × 2:00'
              : `${setup.config.drivesPerPlayer} DRIVES EACH`}
            {norm ? ' · NORMALIZED STATS' : ''}
          </span>
        </div>
      </div>
      <Hints class="hf-versus__hints" items={[['confirm', 'SKIP']]} />
    </div>
  );
}
